import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Detector } from 'react-detect-offline';
import { useHistory } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import { Tooltip } from '@material-ui/core';
import Hidden from '@material-ui/core/Hidden';
import LessonGrid from '@anu/components/LessonGrid';
import ButtonWrapper from '@anu/components/ButtonWrapper';
import { getVisiblePages } from '../utilities/conditionalPages';

const ContentNavigation = ({
  isIntro,
  sections,
  currentLesson,
  nextLesson,
  prevLesson,
  currentIndex,
  isEnabled,
  lessonGridMode,
  hideButtonsLabelsOnMobile,
}) => {
  const history = useHistory();
  const completeAnswer = Drupal.t('Complete all answers to proceed', {}, { context: 'ANU LMS' });

  // Course state scope variables.
  const nextIsLesson = nextLesson && nextLesson.id > 0;
  const nextIsSummary = nextLesson && nextLesson.id === -1;

  // Get pages.
  const {
    prevPage,
    nextPage
  } = getVisiblePages(sections, currentLesson.conditions, currentIndex);

  const updateProgressAndRedirect = () => {
    // Marks lesson as completed if linear progress is enabled for its course.
    if (currentLesson.complete) {
      currentLesson.complete();
    }

    // Redirect to the next page.
    if (!nextPage && !nextIsLesson && !nextIsSummary) {
      window.location.href = currentLesson.finishButtonUrl ?? prevLesson.finishButtonUrl;
      return;
    }
    window.location.href = nextLesson.url;
  };

  const isFirstSection = !currentIndex;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Detector
      polling={false}
      render={({ online }) => {
        const disabled = !online ? false : !isEnabled;
        const buttonProps = {
          variant: 'contained',
          color: 'primary',
          size: 'large',
          endIcon: <ChevronRightIcon />,
          disabled,
        };

        const renderButtonLabel = (label) => {
          if (hideButtonsLabelsOnMobile) {
            return <Hidden smDown>{label}</Hidden>;
          }

          return label;
        };

        const renderButtonWithTooltip = (button) => {
          // "span" is required to display tooltip for disabled buttons.
          return (
            <Tooltip title={disabled ? completeAnswer : ''} arrow>
              <span>{button}</span>
            </Tooltip>
          );
        };

        return (
          <LessonGrid mode={lessonGridMode}>
            <ButtonWrapper>
              {/* PREV buttons */}
              {/* No prev pages, but has prev lesson. */}
              {!prevPage && prevLesson && !isFirstSection && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  aria-label={Drupal.t('Previous', {}, { context: 'ANU LMS' })}
                  startIcon={<ChevronLeftIcon />}
                  href={prevLesson.url}
                  data-test="anu-lms-navigation-previous"
                >
                  {renderButtonLabel(Drupal.t('Previous', {}, { context: 'ANU LMS' }))}
                </Button>
              )}
              {/* No prev pages, but has prev lesson with last page. */}
              {isFirstSection && prevLesson && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  startIcon={<ChevronLeftIcon />}
                  aria-label={Drupal.t('Previous', {}, { context: 'ANU LMS' })}
                  href={`${prevLesson.url}#back`}
                  data-test="anu-lms-navigation-previous"
                >
                  {renderButtonLabel(Drupal.t('Previous', {}, { context: 'ANU LMS' }))}
                </Button>
              )}
              {/* Has a previous page. */}
              {prevPage && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  startIcon={<ChevronLeftIcon />}
                  onClick={() => history.push({ pathname: `/page-${prevPage}` })}
                  aria-label={Drupal.t('Previous', {}, { context: 'ANU LMS' })}
                  data-test="anu-lms-navigation-previous"
                >
                  {renderButtonLabel(Drupal.t('Previous', {}, { context: 'ANU LMS' }))}
                </Button>
              )}

              {/* INTRO button. */}
              {!nextPage && nextIsLesson && isIntro && (
                <Button
                  {...buttonProps}
                  onClick={updateProgressAndRedirect}
                  aria-label={Drupal.t('Start', {}, { context: 'ANU LMS' })}
                  data-test="anu-lms-navigation-start"
                >
                  {renderButtonLabel(Drupal.t('Start', {}, { context: 'ANU LMS' }))}
                </Button>
              )}

              {/* NEXT buttons. */}
              {/* There are more pages in the lesson. */}
              {nextPage &&
                renderButtonWithTooltip(
                  <Button
                    {...buttonProps}
                    onClick={() => history.push({ pathname: `/page-${nextPage}` })}
                    aria-label={Drupal.t('Next', {}, { context: 'ANU LMS' })}
                    data-test="anu-lms-navigation-next"
                  >
                    {renderButtonLabel(Drupal.t('Next', {}, { context: 'ANU LMS' }))}
                  </Button>
                )}
              {/* There are no more pages, but there is a next lesson. */}
              {!nextPage &&
                nextIsLesson &&
                !nextIsSummary &&
                renderButtonWithTooltip(
                  <Button
                    {...buttonProps}
                    onClick={updateProgressAndRedirect}
                    aria-label={Drupal.t('Next', {}, { context: 'ANU LMS' })}
                    data-test="anu-lms-navigation-next"
                  >
                    {renderButtonLabel(Drupal.t('Next', {}, { context: 'ANU LMS' }))}
                  </Button>
                )}

              {/* SUMMARY button. */}
              {/* No next page, no next lesson, but there is a summary. */}
              {!nextPage &&
                !nextIsLesson &&
                nextIsSummary &&
                renderButtonWithTooltip(
                  <Button
                    {...buttonProps}
                    onClick={updateProgressAndRedirect}
                    aria-label={nextLesson.title}
                    data-test="anu-lms-navigation-summary"
                  >
                    {renderButtonLabel(Drupal.t(nextLesson.title, {}, { context: 'ANU LMS' }))}
                  </Button>
                )}

              {/* FINISH button. */}
              {/* No next page, no next lesson, no summary, finish the course. */}
              {!nextPage &&
                !nextIsLesson &&
                !nextIsSummary &&
                renderButtonWithTooltip(
                  <Button
                    {...buttonProps}
                    onClick={updateProgressAndRedirect}
                    aria-label={Drupal.t('Finish', {}, { context: 'ANU LMS' })}
                    data-test="anu-lms-navigation-finish"
                  >
                    {renderButtonLabel(Drupal.t('Finish', {}, { context: 'ANU LMS' }))}
                  </Button>
                )}
            </ButtonWrapper>
          </LessonGrid>
        );
      }}
    />
  );
};

ContentNavigation.propTypes = {
  isIntro: PropTypes.bool,
  sections: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape())),
  currentLesson: PropTypes.shape(),
  nextLesson: PropTypes.shape(),
  prevLesson: PropTypes.shape(),
  currentIndex: PropTypes.number,
  isEnabled: PropTypes.bool,
  lessonGridMode: PropTypes.string,
  hideButtonsLabelsOnMobile: PropTypes.bool,
};

export default ContentNavigation;
